import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent } from "../ui/card";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { employerAPI } from "../../services/api";

/**
 * Phase 1A — non-paid intake inquiry form. Submits to
 * POST /api/employer/intake/inquiry then routes to /employers/intake/success
 * with the returned service reference.
 */
export const IntakeInquiryForm = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    employer_name: "",
    purchaser_name: "",
    purchaser_email: "",
    purchaser_role_title: "",
    purchaser_phone: "",
    purchaser_location: "",
    intake_notes: "",
  });

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const notesLen = form.intake_notes.length;
  const notesOver = notesLen > 4000;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (notesOver) {
      toast.error("Please shorten the description to 4000 characters or fewer.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, (v || "").trim()])
      );
      // Drop empty optional fields so the backend gets nulls.
      for (const k of [
        "purchaser_role_title",
        "purchaser_phone",
        "purchaser_location",
        "intake_notes",
      ]) {
        if (!payload[k]) delete payload[k];
      }
      const res = await employerAPI.submitInquiry(payload);
      navigate(`/employers/intake/success?ref=${encodeURIComponent(res.service_reference)}`);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't submit your inquiry. Please try again.";
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.[0]?.msg || "Please review the form fields and try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <Card className="shadow-lg border-blue-100" data-testid="intake-inquiry-form">
      <CardContent className="p-6 lg:p-8">
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="employer_name">Employer / Organization name *</Label>
              <Input
                id="employer_name"
                required
                maxLength={160}
                value={form.employer_name}
                onChange={update("employer_name")}
                placeholder="e.g. Polished Cafe Ltd."
                data-testid="intake-form-employer-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="purchaser_name">Your name *</Label>
              <Input
                id="purchaser_name"
                required
                maxLength={120}
                value={form.purchaser_name}
                onChange={update("purchaser_name")}
                placeholder="Ada Lin"
                data-testid="intake-form-purchaser-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="purchaser_email">Your email *</Label>
              <Input
                id="purchaser_email"
                type="email"
                required
                value={form.purchaser_email}
                onChange={update("purchaser_email")}
                placeholder="ada@polishedcafe.example"
                data-testid="intake-form-purchaser-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="purchaser_role_title">
                Your role <span className="text-gray-400 text-sm">(optional)</span>
              </Label>
              <Input
                id="purchaser_role_title"
                maxLength={120}
                value={form.purchaser_role_title}
                onChange={update("purchaser_role_title")}
                placeholder="Owner, HR Manager, etc."
                data-testid="intake-form-role-title"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="purchaser_phone">
                Phone <span className="text-gray-400 text-sm">(optional)</span>
              </Label>
              <Input
                id="purchaser_phone"
                maxLength={40}
                value={form.purchaser_phone}
                onChange={update("purchaser_phone")}
                placeholder="555-0100"
                data-testid="intake-form-phone"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="purchaser_location">
                Location <span className="text-gray-400 text-sm">(optional)</span>
              </Label>
              <Input
                id="purchaser_location"
                maxLength={120}
                value={form.purchaser_location}
                onChange={update("purchaser_location")}
                placeholder="Toronto, ON"
                data-testid="intake-form-location"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="intake_notes">Tell us about the role you&apos;re hiring for</Label>
            <Textarea
              id="intake_notes"
              rows={5}
              maxLength={4200}
              value={form.intake_notes}
              onChange={update("intake_notes")}
              placeholder="Share the role, timeline, and priorities so we can tailor your package."
              data-testid="intake-form-notes"
            />
            <div className={`text-xs text-right ${notesOver ? "text-red-600" : "text-gray-500"}`}>
              {notesLen} / 4000
            </div>
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              size="lg"
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-base px-8 shadow-md"
              disabled={submitting}
              data-testid="intake-form-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  Submit inquiry
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            <p className="text-sm text-gray-500 mt-3">
              No payment is collected on this form. We&apos;ll respond with next steps.
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
